import React, { useContext, useMemo, useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Collapse } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import { QmsDataEditModalContext } from "../_common/QmsDataTable";
import qmsTeamActions from "../../../_actions/_qmsActions/qmsTeamActions";
import EmployeeMultiselect from "../../_common/EmployeeMultiselect";
import userActions from "../../../_actions/userActions";
import { USER } from "../../../_constants/actionConstants";
import qmsUserActions from "../../../_actions/_qmsActions/qmsUserActions";
import EditUserModal from "../../AdminTasks/AdminUsers/EditUserModal";
import "./qmsAdmin.css";
import "../_typedefs/qmsTypeDefs.js";
import _ from "lodash";

export default function QmsAdminModal_EditTeam(props) {
  const { divisionId, tabId } = props;

  const qmsDataEditModal = useContext(QmsDataEditModalContext);
  const dispatch = useDispatch();

  // qmsTeamId is copied into component state only as a helper when this modal is being used
  // to add a new team. When adding, only the Name/Description inputs will be shown, without
  // the member assignment area. After adding, the member assignment area will show, with
  const [qmsTeamId, setQmsTeamId] = useState(
    qmsDataEditModal.modalState.data ? qmsDataEditModal.modalState.data.qmsTeamId : null
  );
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [assignedMembers, setAssignedMembers] = useState([]);
  const [unassignedMembers, setUnassignedMembers] = useState([]); // Updated during the current modal session
  const [showMemberDetailsFor, setShowMemberDetailsFor] = useState([]); // array of proDmsUserIds
  const toggleMemberDetails = (proDmsUserId) =>
    showMemberDetailsFor.includes(proDmsUserId)
      ? setShowMemberDetailsFor(showMemberDetailsFor.filter((id) => id !== proDmsUserId))
      : setShowMemberDetailsFor([...showMemberDetailsFor, proDmsUserId]);

  // Fetch qmsTeam
  useEffect(() => qmsTeamId && dispatch(qmsTeamActions.get(qmsTeamId)), [qmsTeamId]);

  const qmsTeam = useSelector((state) =>
    qmsTeamId ? state.qms.openQmsTeams[qmsTeamId] || null : null
  );
  useEffect(() => {
    if (!qmsTeam) return;

    setName(qmsTeam ? qmsTeam.name : "");
    setDescription(qmsTeam ? qmsTeam.description : "");
    setAssignedMembers(qmsTeam && qmsTeam.members ? qmsTeam.members || [] : []);
  }, [qmsTeam]);

  const disableInfoFields = useMemo(
    () => qmsTeam && (qmsTeam.isQualityManagementRep || qmsTeam.isQualityManagementTeam),
    [qmsTeam]
  );

  // Error object
  const [formErrors, _setFormErrors] = useState({ name: null, description: null });
  const setFormError = (fieldName, message) =>
    _setFormErrors({ ...formErrors, [fieldName]: message });
  const clearFormError = (fieldName) =>
    _setFormErrors({ ...formErrors, [fieldName]: null });

  const qmsTeamMemberRoles = useSelector((state) => state.qms.qmsTeamMemberRoles || null);
  const memberRoleOptions = useMemo(
    () =>
      qmsTeam
        ? _.orderBy(
            (qmsTeamMemberRoles || []).filter((r) =>
              qmsTeam.isQualityManagementTeam ? r.value >= 5 : r.value <= 4
            ),
            "value",
            "asc"
          )
        : [],
    [qmsTeamMemberRoles, qmsTeam]
  );

  // Debug Logging //////////////////////////////////////////////////////////////////////
  // useEffect(() => console.log("assignedMembers=", assignedMembers), [assignedMembers]);

  // Component Logic ////////////////////////////////////////////////////////////////////
  const toggleModal = () => {
    if (qmsDataEditModal.modalState.showModal)
      dispatch({ type: USER.CLEAR_EMPLOYEES_LIST, tabId });
    qmsDataEditModal.toggleModal();
  };

  const unassignMember = (proDmsUserId) => {
    const qmsUserToUnassign = assignedMembers.find(
      (m) => m.proDmsUserId === proDmsUserId
    );
    setAssignedMembers(assignedMembers.filter((m) => m.proDmsUserId !== proDmsUserId));

    if (qmsUserToUnassign)
      setUnassignedMembers([...unassignedMembers, qmsUserToUnassign]);
  };

  /**
   * Passed as callback prop to RegisterUserModal
   * @param {Boolean} mustUpdateEmployee
   * @returns
   */
  const assignMember =
    (mustUpdateEmployee, employeeId = null) =>
    async (userId) => {
      const recoverableAssignment = unassignedMembers.find(
        (m) => m.proDmsUserId === userId
      );

      if (recoverableAssignment) {
        setAssignedMembers([recoverableAssignment, ...assignedMembers]);
        setUnassignedMembers(
          unassignedMembers.filter((um) => um.proDmsUserId !== userId)
        );
      } else {
        // Attempt to get QmsUser by ProDmsUserId. (The 'userId' from param is ProDmsUserId,
        // provided from callback from Register User modal.) If QmsUser does not exist,
        // the ProDMS user's data will be temporarily stored until user clicks 'Update'.
        const [user, qmsUser] = await Promise.all([
          dispatch(userActions.getInfoById(userId)),
          dispatch(qmsUserActions.getByProDmsUserId(userId)),
        ]);
        /** @type {QmsTeamMemberDto} */
        const qmsTeamMember = {
          qmsUserId: qmsUser ? qmsUser.qmsUserId : 0,
          userRoleValue: qmsUser ? qmsUser.userRoleValue : 1, // UserRole of QMS_USER (lowest level)
          divisionId,
          proDmsUserId: userId,
          proDmsUserUnitId: user.unitId,
          district: user.unit.district,
          unitNumber: user.unit.unitNumber,
          distUnit: user.unit.distUnit,
          qmsTeamId,
          memberRoleValue: 0,
          joinedOn: new Date().toISOString(), // Server will overwrite
          firstName: user.firstName,
          middleInitial: user.middleInitial,
          lastName: user.lastName,
          fullName: user.fullName,
          employeeNo: user.employeeNo,
          email: `${user.employeeNo}@dot.ca.gov`,
          phoneNumber: "",
        };
        setAssignedMembers([qmsTeamMember, ...assignedMembers]);
        if (!(showMemberDetailsFor || []).includes(userId))
          setShowMemberDetailsFor([...showMemberDetailsFor, userId]);

        if (mustUpdateEmployee)
          dispatch({
            type: USER.UPDATE_EMPLOYEE,
            tabId,
            employeeId,
            fields: {
              isRegisteredInProDMS: true,
              userId: user.userId,
              unitId: user.unitId,
              district: user.unit.district,
              unit: user.unit.unitNumber,
            },
          });
      }
    };

  const validate = (fieldName) => {
    switch (fieldName) {
      case "name":
        if (!name) setFormError(fieldName, "Required");
        else clearFormError(fieldName);
        break;
      default:
        break;
    }
    return;
  };

  const formIsInvalid = () => Object.values(formErrors).some((e) => e !== null);

  // Event handlers /////////////////////////////////////////////////////////////////////

  const handleClick_add = async (e) => {
    e.preventDefault();
    const newQmsTeam = { divisionId, name, description, members: [] };
    const newQmsTeamId = await dispatch(qmsTeamActions.add(newQmsTeam));
    setQmsTeamId(newQmsTeamId);
  };

  const handleClick_update = async (e) => {
    e.preventDefault();

    if (assignedMembers.some((m) => !m.memberRoleValue)) return;

    const qmsTeamData = {
      qmsTeamId,
      divisionId,
      name,
      description,
      isQualityManagementTeam: qmsTeam ? qmsTeam.isQualityManagementTeam : false,
      isQualityManagementRep: qmsTeam ? qmsTeam.isQualityManagementRep : false,
      members: assignedMembers.map((m) => ({
        qmsUserId: m.qmsUserId,
        userRoleValue: m.userRoleValue,
        divisionId: m.divisionId,
        proDmsUserId: m.proDmsUserId,
        proDmsUserUnitId: m.proDmsUserUnitId,
        district: m.district,
        unitNumber: "", // Not used for update
        qmsTeamId,
        memberRoleValue: m.memberRoleValue,
        memberRoleDisplayName: "", // Not used for update
        joinedOn: m.joinedOn,
        firstName: m.firstName,
        middleInitial: m.middleInitial || "",
        lastName: m.lastName,
        employeeNo: m.employeeNo,
        email: m.email,
        phoneNumber: m.phoneNumber,
      })),
    };
    dispatch(qmsTeamActions.update(qmsTeamData));
    toggleModal();
  };

  const handleChange_memberRole = (proDmsUserId) => (e) => {
    const _assignedMembers = assignedMembers.map((m) => ({ ...m }));

    let assignedMemberToUpdate = _assignedMembers.find(
      (m) => m.proDmsUserId === proDmsUserId
    );
    if (!assignedMemberToUpdate) return;

    assignedMemberToUpdate.memberRoleValue = +e.target.value;
    setAssignedMembers(_assignedMembers);
  };

  const handleClick_removeAssignment = (proDmsUserId) => (e) => {
    e.preventDefault();
    unassignMember(proDmsUserId);
  };

  const handleClick_joinedOn = (proDmsUserId) => (e) => {
    e.preventDefault();
  };

  const closeBtn = (
    <button className="close" onClick={toggleModal}>
      <FontAwesomeIcon icon="times" className="fa-fw text-white" />
    </button>
  );

  const renderTeamInfoFields = () => (
    <>
      {disableInfoFields && (
        <div className="alert alert-warning mb-2 p-1 w-100">
          <div className="d-flex flex-row">
            <div className="my-auto align-middle">
              <FontAwesomeIcon icon="exclamation-triangle" className="fa-fw mx-2" />
            </div>
            <div className="flex-grow-1 my-auto ml-1">
              Team ID and Name/Description for user groups designated as QMT/QMR are
              locked. To edit, please send a request to the system administrator.
            </div>
          </div>
        </div>
      )}
      <div className="form-row form-group mb-2">
        <label htmlFor="name" className="col-sm-3 col-form-label my-auto">
          <b>Team Id:</b>*
        </label>
        <div className="col-sm-5">
          <input
            type="text"
            name="name"
            id="name"
            className="form-control"
            maxLength="10"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={disableInfoFields}
          />
          {formErrors.name && (
            <span className="error-small">
              <small>{formErrors.name}</small>
            </span>
          )}
        </div>
      </div>
      <div className="form-row form-group mb-2">
        <label htmlFor="description" className="col-sm-3 col-form-label my-auto">
          <b>Name/Description:</b>*
        </label>
        <div className="col-sm-9">
          <input
            type="text"
            name="description"
            id="description"
            className="form-control"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={disableInfoFields}
          />
        </div>
      </div>
    </>
  );

  const renderMemberSelector = () => (
    <div className="d-flex flex-row w-100">
      <div className="d-flex flex-column w-50 pr-1">
        <QmsTeamMemberSelector
          tabId={tabId}
          assignedMembers={assignedMembers}
          unassignedMembers={unassignedMembers}
          assignMember={assignMember}
          unassignMember={unassignMember}
        />
      </div>
      <div className="d-flex flex-column w-50 pl-1">
        <div className="card flex-grow-1 bg-lightgray">
          <div className="card-body">
            <h5 className="">Assigned Members</h5>
            {assignedMembers && assignedMembers.length ? (
              assignedMembers.map((m) => renderSelectedMembers(m))
            ) : (
              <div className="card my-2 mx-0 qta-rt__item">
                <div className="card-body p-2">No assigned members</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSelectedMembers = (member) => {
    // Use proDmsUserId as unique identifier, NOT qmsUserId,
    // since qmsUserId may not have been issued yet
    const showDetails = showMemberDetailsFor.includes(member.proDmsUserId);

    return (
      <div
        key={`qmsTeamAssignmentRowKey-${member.proDmsUserId}`}
        className={`card my-2 mx-0 qta-rt__item ${showDetails ? "border-secondary" : ""}`}
      >
        <div className="card-body p-2">
          <div
            className="qta-rt__item-head"
            onClick={() => toggleMemberDetails(member.proDmsUserId)}
          >
            <div className="qta-rt__item-head--collapse">
              <FontAwesomeIcon
                icon={showDetails ? "caret-down" : "caret-right"}
                className="fa-fw icon align-middle"
              />
            </div>
            <div className="qta-rt__item-head--name d-flex flex-row flex-wrap">
              <div className="mr-2">
                <span
                  className="align-middle"
                  style={{ fontWeight: showDetails ? "bold" : "normal" }}
                >
                  {member.fullName}
                </span>
              </div>
              <div>
                <span className="align-middle text-small" style={{ color: "gray" }}>
                  ({member.distUnit})
                </span>
              </div>
            </div>
            <div className="qta-rt__item-head--role d-flex flex-row flex-wrap">
              <div className="qta-rt__item-head--role-item ">
                <QmsTeamRoleBadge roleId={member.memberRoleValue} />
              </div>
            </div>
            <div className="qta-rt__item-head--unassign">
              <div className="float-right">
                <button
                  name="unassign"
                  id={`unassignBtn_${member.proDmsUserId}`}
                  className="btn btn-sm btn-outline-danger p-1"
                  onClick={handleClick_removeAssignment(member.proDmsUserId)}
                  // disabled={permissions.teams.delete < 2 || fetching}
                >
                  <FontAwesomeIcon icon="times" className="fa-fw" />
                </button>
              </div>
            </div>
          </div>
          <Collapse isOpen={showDetails}>
            <hr className="my-2" />
            <div className="row mb-1 ml-2" onClick={(e) => e.stopPropagation()}>
              <label
                htmlFor={`projectRoleId_${member.proDmsUserId}`}
                className="col-sm-4 col-form-label pl-3"
              >
                <b>Team Role:</b>*
              </label>
              <div className="col-sm-8">
                <select
                  name="memberRoleValue"
                  id={`qmsTeamMemberRoleId_${member.proDmsUserId}`}
                  className="form-control w-100"
                  value={member.memberRoleValue}
                  onChange={handleChange_memberRole(member.proDmsUserId)}
                  // onBlur={handleBlur(member.proDmsUserId, "memberRoleValue")}
                  // disabled={permissions.teams.edit < 1 || fetching}
                >
                  <option key="qmsTeamMemberRoleId_0" value={0}>
                    (Select Team Role)
                  </option>
                  {memberRoleOptions.map((ro) => (
                    <option key={`qmsTeamMemberRoleId_${ro.value}`} value={ro.value}>
                      {ro.displayName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="row mb-1 ml-2">
              <div className="col-sm-4"></div>
              <div className="col-sm-8">
                <label className="mr-2">
                  <b>Joined On:</b>
                </label>
                <button
                  type="button"
                  name="joinedOn"
                  id={`joinedOn_${member.proDmsUserId}`}
                  className={
                    member.joinedOn
                      ? "btn btn-outline-info btn-sm"
                      : "btn btn-outline-danger btn-sm"
                  }
                  onClick={handleClick_joinedOn(member.proDmsUserId)}
                  // disabled={
                  //   !permissions.includes(docPermConstants.EDIT_LEVEL_2) || fetching
                  // }
                >
                  {member.joinedOn
                    ? new Date(member.joinedOn).toLocaleDateString()
                    : "(N/A)"}
                </button>
              </div>
            </div>
          </Collapse>
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal
        isOpen={qmsDataEditModal.modalState.showModal}
        toggle={toggleModal}
        fade={false}
        size={"xl"}
      >
        <ModalHeader toggle={toggleModal} className="bg-info text-white" close={closeBtn}>
          <FontAwesomeIcon icon={qmsTeamId ? "edit" : "plus"} className="fa-fw mr-2" />
          {qmsTeamId ? `Edit ` : `Add New `}
          Team
        </ModalHeader>
        <ModalBody>
          <div className="card mb-2">
            <div className="card-body p-2">{renderTeamInfoFields()}</div>
          </div>
          {qmsTeamId && renderMemberSelector()}
        </ModalBody>
        <ModalFooter className="bg-lightgray">
          <div className="form-row form-group mb-0">
            <button
              className="btn btn-primary"
              name="add"
              onClick={qmsTeamId ? handleClick_update : handleClick_add}
            >
              {qmsTeamId ? "Update" : "Add"}
            </button>
            <button
              className="btn btn-secondary ml-2"
              color="secondary"
              onClick={toggleModal}
            >
              Cancel
            </button>
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
}

function QmsTeamMemberSelector(props) {
  const { tabId, assignedMembers, unassignedMembers, assignMember, unassignMember } =
    props;

  const employeeList = useSelector(
    (state) => state.users.openEmployeeLists[tabId] || null
  );

  const [modalState_registerUser, setModalState_registerUser] = useState({
    showModal: false,
    data: null,
  });
  const toggleModal_registerUser = () =>
    setModalState_registerUser({
      ...modalState_registerUser,
      showModal: !modalState_registerUser.showModal,
    });

  const selectedEmployees = useMemo(() => {
    const assignedMemberProDmsUserIds = assignedMembers.map((m) => m.proDmsUserId);
    return (employeeList || []).filter(
      (e) => e.userId && assignedMemberProDmsUserIds.includes(e.userId)
    );
  }, [employeeList, assignedMembers]);

  const deselectedEmployees = useMemo(() => {
    const unassignedMemberProDmsUserIds = unassignedMembers.map((m) => m.proDmsUserId);
    return (employeeList || []).filter(
      (e) => e.userId && unassignedMemberProDmsUserIds.includes(e.userId)
    );
  }, [employeeList, unassignedMembers]);

  const handleSelect_employee = (employee) => {
    // If the selected employee is registered in ProDMS, userId exists, so
    // provide this userId to add to the projStaffList.
    const mustUpdateEmployee = false;
    if (employee.isRegisteredInProDMS)
      assignMember(mustUpdateEmployee, employee.employeeId)(employee.userId);
    // If not, use state.selectedEmployeeData as temp storage for retrieval
    // later by EditUserModal. Employee data is used for prepopulating fields
    // in EditUserModal.
    else {
      setModalState_registerUser({
        showModal: !modalState_registerUser.showModal,
        data: {
          employeeId: employee.employeeId,
          firstName: employee.firstName,
          middleInitial: employee.middleInitial,
          lastName: employee.lastName,
          employeeNo: employee.employeeNo,
        },
      });
    }
  };

  const handleDeselect_employee = (employee) => unassignMember(employee.userId);

  return (
    <>
      <div className="card flex-grow-1">
        <div className="card-body">
          <div>
            <EmployeeMultiselect
              tabId={tabId}
              handleSelect={handleSelect_employee}
              handleDeselect={handleDeselect_employee}
              selectedItems={selectedEmployees}
              deletedItems={deselectedEmployees}
              disabledItems={[]}
              disableProDMSUsers={false}
            />
          </div>
        </div>
      </div>
      {modalState_registerUser.showModal && (
        <EditUserModal
          tabId={tabId}
          isNewUser={true}
          user={modalState_registerUser.data}
          showModal={modalState_registerUser.showModal}
          toggleModal={toggleModal_registerUser}
          submitCallback={assignMember(true, modalState_registerUser.data.employeeId)}
          allowRegisterAsOfficeAdmin={false}
          acceptCaption={`Register & Assign`}
        />
      )}
    </>
  );
}

function QmsTeamRoleBadge(props) {
  const { roleId } = props;
  const qmsTeamMemberRoles = useSelector((state) => state.qms.qmsTeamMemberRoles || null);
  const qtmRole = (qmsTeamMemberRoles || []).find((qtmr) => qtmr.value === roleId);

  return (
    <span
      className={`align-middle span-bubble p-1 ${roleId ? "bg-secondary" : "bg-danger"}`}
    >
      {roleId && qtmRole ? `${qtmRole.displayName || "(Role N/A)"}` : "(No Role)"}
    </span>
  );
}
